import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User, UserCredential } from 'firebase/auth';

import {
  createEmailPasswordUser,
  fetchSignInMethodsForEmailAddress,
  getGoogleRedirectResult,
  isNewUserCredential,
  sendPasswordResetEmailAddress,
  signInWithCustomTokenValue,
  signInWithEmailPassword,
  signInWithGooglePopup,
  signInWithGoogleRedirect,
  signOutUser,
  subscribeToAuthChanges,
} from './firebase';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  signInWithCustomToken: (token: string) => Promise<UserCredential>;
  signInWithEmailAndPassword: (
    email: string,
    password: string,
  ) => Promise<UserCredential>;
  lookupEmailProviders?: (email: string) => Promise<string[]>;
  createUserWithEmailAndPassword?: (
    email: string,
    password: string,
  ) => Promise<UserCredential>;
  sendPasswordResetEmail?: (email: string) => Promise<void>;
  signInWithGooglePopup?: () => Promise<UserCredential>;
  signInWithGoogleRedirect?: () => Promise<void>;
  getGoogleRedirectResult?: () => Promise<UserCredential | null>;
  /** Whether a sign-in created its Firebase account; unknown counts as new. */
  isNewUser?: (credential: UserCredential) => Promise<boolean>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

function getInjectedTestUser(): User | null {
  if (typeof window === 'undefined' || !window.__KUDU_E2E_AUTH__) {
    return null;
  }

  const { email = 'e2e@example.com', uid = 'e2e-user', ...rest } =
    window.__KUDU_E2E_AUTH__;

  return {
    email,
    getIdToken: viaductGetIdToken,
    ...rest,
    providerData: [{ uid }],
    uid,
  } as unknown as User;
}

async function viaductGetIdToken() {
  return 'e2e-token';
}

function getAuthCacheIdentity(user: User | null): string | null {
  if (!user) {
    return null;
  }

  return user.uid || user.email || user.providerData[0]?.uid || null;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const authCacheIdentityRef = useRef<string | null>(null);

  const setAuthUser = useCallback(
    (nextUser: User | null) => {
      const nextIdentity = getAuthCacheIdentity(nextUser);

      if (authCacheIdentityRef.current !== nextIdentity) {
        queryClient.clear();
      }

      authCacheIdentityRef.current = nextIdentity;
      setUser(nextUser);
    },
    [queryClient],
  );

  useEffect(() => {
    const injectedTestUser = getInjectedTestUser();

    if (injectedTestUser) {
      setAuthUser(injectedTestUser);
      setLoading(false);
      return;
    }

    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      try {
        unsubscribe = await subscribeToAuthChanges((nextUser) => {
          if (!isMounted) {
            return;
          }

          setAuthUser(nextUser);
          setLoading(false);
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to initialize Firebase Auth', error);
        setAuthUser(null);
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, [setAuthUser]);

  const value: AuthContextValue = {
    user,
    loading,
    getIdToken: async (forceRefresh = false) => {
      if (!user) {
        return null;
      }

      return user.getIdToken(forceRefresh);
    },
    signInWithCustomToken: async (token) => {
      const credential = await signInWithCustomTokenValue(token);
      setAuthUser(credential.user);
      return credential;
    },
    signInWithEmailAndPassword: async (email, password) => {
      const credential = await signInWithEmailPassword(email, password);
      setAuthUser(credential.user);
      return credential;
    },
    lookupEmailProviders: (email) =>
      fetchSignInMethodsForEmailAddress(email),
    createUserWithEmailAndPassword: async (email, password) => {
      const credential = await createEmailPasswordUser(email, password);
      setAuthUser(credential.user);
      return credential;
    },
    sendPasswordResetEmail: (email) =>
      sendPasswordResetEmailAddress(email),
    signInWithGooglePopup: async () => {
      const credential = await signInWithGooglePopup();
      setAuthUser(credential.user);
      return credential;
    },
    signInWithGoogleRedirect: () => signInWithGoogleRedirect(),
    getGoogleRedirectResult: async () => {
      const credential = await getGoogleRedirectResult();

      if (credential) {
        setAuthUser(credential.user);
      }

      return credential;
    },
    isNewUser: (credential) => isNewUserCredential(credential),
    signOut: async () => {
      if (typeof window !== 'undefined' && window.__KUDU_E2E_AUTH__) {
        window.__KUDU_E2E_AUTH__ = null;
        setAuthUser(null);
        return;
      }

      await signOutUser();
      setAuthUser(null);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
